import { useEffect } from 'react';

import { cn } from '@utils/cn';

import { useAnimatedMount } from '@ui-component-library/hooks/useAnimatedMount';

import {
  dialogBackdropClasses,
  dialogContentClasses,
  dialogFooterClasses,
  dialogHeaderClasses,
  dialogWrapperClasses
} from './Dialog.styles';
import type {
  DialogContentProps,
  DialogFooterProps,
  DialogHeaderProps,
  DialogProps
} from './Dialog.types';

export const Dialog = ({
  isOpen = false,
  onClose,
  children,
  size = 'medium',
  tabIndex = 0,
  className,
  hideBackdrop = false,
  ...props
}: DialogProps) => {
  const { shouldRender, animateIn, ref, onTransitionEnd } = useAnimatedMount(isOpen);

  useEffect(() => {
    if (!onClose) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      className={cn(dialogBackdropClasses({ hideBackdrop }))}
      style={{
        opacity: animateIn ? 1 : 0,
        transition: 'opacity 300ms ease-out'
      }}
    >
      <div
        ref={ref}
        className={cn(dialogWrapperClasses({ size }), className)}
        style={{
          transform: animateIn ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 400ms ease-out'
        }}
        onTransitionEnd={onTransitionEnd}
        tabIndex={tabIndex}
        {...props}
      >
        {children}
      </div>
    </div>
  );
};

const DialogHeader = ({ children, className, ...props }: DialogHeaderProps) => (
  <div className={cn(dialogHeaderClasses(), className)} {...props}>
    {children}
  </div>
);

const DialogContent = ({ children, className, ...props }: DialogContentProps) => (
  <div className={cn(dialogContentClasses(), className)} {...props}>
    {children}
  </div>
);

const DialogFooter = ({ children, className, ...props }: DialogFooterProps) => (
  <div className={cn(dialogFooterClasses(), className)} {...props}>
    {children}
  </div>
);

Dialog.Header = DialogHeader;
Dialog.Content = DialogContent;
Dialog.Footer = DialogFooter;
